import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, TrendingUp, Award } from 'lucide-react';

interface RadarDataPoint {
  label: string;
  value: number;
  color?: string;
  maxValue?: number;
}

interface AnimatedRadarChartProps {
  title: string;
  data: RadarDataPoint[];
  size?: number;
}

const AnimatedRadarChart: React.FC<AnimatedRadarChartProps> = ({ 
  title, 
  data,
  size = 400 
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const centerX = size / 2;
  const centerY = size / 2;
  const maxRadius = size * 0.35;
  const levels = 5;

  // Calculate points for each data item
  const angleStep = (2 * Math.PI) / data.length;
  const points = data.map((item, index) => {
    const angle = angleStep * index - Math.PI / 2; // Start from top
    const maxVal = item.maxValue || 100;
    const normalizedValue = (item.value / maxVal) * maxRadius;
    
    return {
      x: centerX + normalizedValue * Math.cos(angle),
      y: centerY + normalizedValue * Math.sin(angle),
      labelX: centerX + (maxRadius + 50) * Math.cos(angle),
      labelY: centerY + (maxRadius + 50) * Math.sin(angle),
      angle,
      ...item,
    };
  });

  // Create path for the data polygon
  const dataPath = points.map((p, i) => 
    `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
  ).join(' ') + ' Z';

  return (
    <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
      <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">{title}</h3>

      <div className="flex flex-col lg:flex-row items-center gap-8">
        {/* Radar Chart */}
        <div className="relative" style={{ width: size, height: size }}>
          <svg width={size} height={size}>
            {/* Background Circles (Levels) */}
            {Array.from({ length: levels }).map((_, i) => {
              const radius = (maxRadius / levels) * (i + 1);
              return (
                <g key={`level-${i}`}>
                  <circle
                    cx={centerX}
                    cy={centerY}
                    r={radius}
                    fill="none"
                    stroke="#E5E7EB"
                    strokeWidth="1"
                    strokeDasharray={i === levels - 1 ? '0' : '4 4'}
                  />
                  {/* Level Label */}
                  <text
                    x={centerX + 5}
                    y={centerY - radius + 5}
                    className="text-xs fill-gray-400"
                  >
                    {((i + 1) * 20)}
                  </text>
                </g>
              );
            })}

            {/* Axis Lines */}
            {points.map((point, index) => (
              <motion.line
                key={`axis-${index}`}
                x1={centerX}
                y1={centerY}
                x2={centerX + maxRadius * Math.cos(point.angle)}
                y2={centerY + maxRadius * Math.sin(point.angle)}
                stroke="#D1D5DB"
                strokeWidth="1"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.8, delay: index * 0.05 }}
              />
            ))}

            {/* Data Polygon - Fill */}
            <motion.path
              d={dataPath}
              fill="url(#radarGradient)"
              fillOpacity="0.3"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              style={{ transformOrigin: `${centerX}px ${centerY}px` }}
            />

            {/* Data Polygon - Stroke */}
            <motion.path
              d={dataPath}
              fill="none"
              stroke="url(#radarStroke)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, delay: 0.5 }}
            />

            {/* Data Points */}
            {points.map((point, index) => {
              const isHovered = hoveredIndex === index;
              
              return (
                <g key={`point-${index}`}>
                  {/* Glow Effect */}
                  {isHovered && (
                    <motion.circle
                      cx={point.x}
                      cy={point.y}
                      r="12"
                      fill={point.color || '#3B82F6'}
                      opacity="0.3"
                      initial={{ scale: 0 }}
                      animate={{ scale: [1, 1.5, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    />
                  )}

                  {/* Point */}
                  <motion.circle
                    cx={point.x}
                    cy={point.y}
                    r="6"
                    fill={point.color || '#3B82F6'}
                    stroke="white"
                    strokeWidth="2"
                    initial={{ scale: 0 }}
                    animate={{ scale: isHovered ? 1.5 : 1 }}
                    transition={{ duration: 0.8, delay: index * 0.1 + 0.5 }}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    className="cursor-pointer"
                    style={{
                      filter: isHovered ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))' : 'none',
                    }}
                  />

                  {/* Value Label on Hover */}
                  {isHovered && (
                    <motion.g
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <rect
                        x={point.x - 25}
                        y={point.y - 35}
                        width="50"
                        height="24"
                        rx="4"
                        fill={point.color || '#3B82F6'}
                      />
                      <text
                        x={point.x}
                        y={point.y - 18}
                        textAnchor="middle"
                        className="text-sm font-bold fill-white"
                      >
                        {point.value}
                      </text>
                    </motion.g>
                  )}
                </g>
              );
            })}

            {/* Gradients */}
            <defs>
              <linearGradient id="radarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.6" />
              </linearGradient>
              <linearGradient id="radarStroke" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3B82F6" />
                <stop offset="100%" stopColor="#8B5CF6" />
              </linearGradient>
            </defs>
          </svg>

          {/* Axis Labels */}
          {points.map((point, index) => {
            const isHovered = hoveredIndex === index;

            return (
              <motion.div
                key={`label-${index}`}
                className="absolute"
                style={{
                  left: point.labelX,
                  top: point.labelY,
                  transform: 'translate(-50%, -50%)',
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: isHovered ? 1.1 : 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 + 0.7 }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div className={`
                  px-3 py-2 rounded-lg text-center cursor-pointer transition-all
                  ${isHovered
                    ? 'bg-gradient-to-br from-blue-500 to-purple-500 text-white shadow-lg scale-110'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}>
                  <div className="text-xs font-semibold whitespace-nowrap">
                    {point.label}
                  </div>
                  {isHovered && (
                    <div className="text-xs font-bold mt-1">
                      {point.value}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}

          {/* Center Icon */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1 }}
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shadow-xl">
              <Star className="w-8 h-8 text-white" />
            </div>
          </motion.div>
        </div>

        {/* Legend & Stats */}
        <div className="flex-1 space-y-4 max-w-md">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-200">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Award className="w-5 h-5 text-blue-600" />
              Performance Metrics
            </h4>

            <div className="space-y-3">
              {points.map((point, index) => {
                const isHovered = hoveredIndex === index;
                const percentage = ((point.value / (point.maxValue || 100)) * 100).toFixed(1);

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 + 0.8 }}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    className={`
                      p-3 rounded-lg cursor-pointer transition-all
                      ${isHovered
                        ? 'bg-white shadow-lg scale-105'
                        : 'bg-white/50 hover:bg-white'
                      }
                    `}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{point.label}</span>
                      <span className="text-lg font-bold text-blue-600">{point.value}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{
                            background: `linear-gradient(90deg, ${point.color || '#3B82F6'}, ${point.color || '#8B5CF6'})`
                          }}
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 1, delay: index * 0.1 + 1 }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-gray-600 w-12 text-right">
                        {percentage}%
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Summary Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5 }}
            className="grid grid-cols-2 gap-3"
          >
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 text-center border border-green-200">
              <TrendingUp className="w-6 h-6 mx-auto text-green-600 mb-2" />
              <div className="text-2xl font-bold text-gray-900">
                {Math.max(...data.map(d => d.value))}
              </div>
              <div className="text-xs text-gray-600">Highest</div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 text-center border border-amber-200">
              <Star className="w-6 h-6 mx-auto text-amber-600 mb-2" />
              <div className="text-2xl font-bold text-gray-900">
                {(data.reduce((sum, d) => sum + d.value, 0) / data.length).toFixed(1)}
              </div>
              <div className="text-xs text-gray-600">Average</div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AnimatedRadarChart;

